/**
 * receipt-html.js — Única fuente de verdad para el diseño del recibo.
 *
 * Genera el HTML del recibo como string. Usado por:
 * - receipt-detail.js (abrir en ventana nueva, imprimir, descargar PDF, compartir)
 * - El backend (templates/receipt-template.js) tiene su propia copia
 *   porque no puede importar desde el dashboard — pero el diseño
 *   debe mantenerse sincronizado con este archivo.
 *
 * Para personalizar el diseño, editar SOLO este archivo.
 */

import logoUrl from '../assets/images/dashboard/main-logo.png';

/**
 * Genera el HTML completo del recibo.
 * @param {object} receipt - Datos del recibo
 * @param {object} options - { withToolbar: true } incluye toolbar con botones
 */
export const generateReceiptHTML = (receipt, options = {}) => {
    if (!receipt) return '';

    const withToolbar = options.withToolbar || false;

    const sym = getCurrencySymbol(receipt.currency);
    const formattedDate = new Date(receipt.createdAt).toLocaleDateString('es-VE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });

    const itemsRows = (receipt.items || [])
        .map(
            (item, i) => `
        <tr>
            <td style="padding: 10px 12px; border-bottom: 1px solid #eee;">${i + 1}</td>
            <td style="padding: 10px 12px; border-bottom: 1px solid #eee;">${item.name}</td>
            <td style="padding: 10px 12px; border-bottom: 1px solid #eee; text-align: center;">${item.quantity}</td>
            <td style="padding: 10px 12px; border-bottom: 1px solid #eee; text-align: right;">${sym} ${formatAmount(item.unitPrice, receipt.currency)}</td>
            <td style="padding: 10px 12px; border-bottom: 1px solid #eee; text-align: right;">${sym} ${formatAmount(item.subtotal, receipt.currency)}</td>
        </tr>`,
        )
        .join('');

    const holderIdBlock = receipt.holderId
        ? `<div class="info-block">
                <div class="label">${receipt.holderId.length > 10 ? 'RIF' : 'RIF/Cédula'}</div>
                <div class="value">${receipt.holderId}</div>
            </div>`
        : '';

    const holderPhoneBlock = receipt.holderPhone
        ? `<div class="info-block"><div class="label">Teléfono</div><div class="value">${receipt.holderPhone}</div></div>`
        : '';

    const holderContactBlock = (receipt.holderId || receipt.holderPhone)
        ? `<div class="info-row">
                ${holderIdBlock}
                ${holderPhoneBlock}
            </div>`
        : '';

    const holderAddressBlock = receipt.holderAddress
        ? `<div class="info-row">
                <div class="info-block"><div class="label">Dirección</div><div class="value">${receipt.holderAddress}</div></div>
            </div>`
        : '';

    const namePart = (receipt.holderName || 'Cliente').substring(0, 10).replace(/\s+/g, '');
    const filename = `${namePart}-${receipt.receiptNumber}.pdf`;

    const toolbarBlock = withToolbar ? `
        <div class="toolbar" id="receipt-toolbar">
            <button onclick="window.print()" class="toolbar-btn">
                <span class="toolbar-icon">🖨</span> Imprimir
            </button>
            <button onclick="downloadPdf()" class="toolbar-btn" id="btn-download">
                <span class="toolbar-icon">📥</span> Descargar PDF
            </button>
            <button onclick="sharePdf()" class="toolbar-btn" id="btn-share">
                <span class="toolbar-icon">📤</span> Compartir
            </button>
        </div>` : '';

    const toolbarScript = withToolbar ? `
    <script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.2/html2pdf.bundle.min.js"></script>
    <script>
        var receiptFilename = '${filename}';

        function downloadPdf() {
            var btn = document.getElementById('btn-download');
            btn.disabled = true;
            btn.innerHTML = '<span class="toolbar-icon">⏳</span> Generando...';
            var element = document.getElementById('receipt-container');
            var opt = {
                margin: 10,
                filename: receiptFilename,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true },
                js2pdf: { unit: 'mm', format: 'letter', orientation: 'portrait' },
                pagebreak: { mode: 'avoid-all' }
            };
            html2pdf().set(opt).from(element).save().then(function() {
                btn.disabled = false;
                btn.innerHTML = '<span class="toolbar-icon">📥</span> Descargar PDF';
            }).catch(function(err) {
                console.error(err);
                alert('Error al generar el PDF');
                btn.disabled = false;
                btn.innerHTML = '<span class="toolbar-icon">📥</span> Descargar PDF';
            });
        }

        function sharePdf() {
            var btn = document.getElementById('btn-share');
            btn.disabled = true;
            btn.innerHTML = '<span class="toolbar-icon">⏳</span> Generando...';
            var element = document.getElementById('receipt-container');
            var opt = {
                margin: 10,
                filename: receiptFilename,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true },
                js2pdf: { unit: 'mm', format: 'letter', orientation: 'portrait' },
                pagebreak: { mode: 'avoid-all' }
            };
            html2pdf().set(opt).from(element).outputPdf('blob').then(function(blob) {
                var file = new File([blob], receiptFilename, { type: 'application/pdf' });
                if (navigator.canShare && navigator.canShare({ files: [file] })) {
                    navigator.share({
                        files: [file],
                        title: 'Recibo — Aguaman',
                        text: 'Recibo ${receipt.receiptNumber} - ${receipt.holderName}'
                    }).catch(function(err) {
                        if (err.name !== 'AbortError') alert('Error al compartir');
                    });
                } else {
                    var url = URL.createObjectURL(blob);
                    var a = document.createElement('a');
                    a.href = url;
                    a.download = receiptFilename;
                    a.click();
                    URL.revokeObjectURL(url);
                }
                btn.disabled = false;
                btn.innerHTML = '<span class="toolbar-icon">📤</span> Compartir';
            }).catch(function(err) {
                console.error(err);
                alert('Error al generar el PDF');
                btn.disabled = false;
                btn.innerHTML = '<span class="toolbar-icon">📤</span> Compartir';
            });
        }
    </script>` : '';

    return `
<!DOCTYPE html>
<html lang="es">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Recibo #${receipt.receiptNumber} — Aguaman</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
            font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
            background-color: #f5f5f5;
            padding: 20px;
            color: #333;
        }
        .toolbar {
            max-width: 800px;
            margin: 0 auto 15px auto;
            display: flex;
            gap: 10px;
        }
        .toolbar-btn {
            padding: 8px 16px;
            border: 1px solid #00a4d3;
            background: #fff;
            color: #00a4d3;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 600;
            transition: background 0.2s, color 0.2s;
        }
        .toolbar-btn:hover { background: #00a4d3; color: #fff; }
        .toolbar-btn:disabled { opacity: 0.6; cursor: wait; }
        .toolbar-icon { margin-right: 4px; }
        .receipt-container { max-width: 800px; margin: 0 auto; background: #fff; }
        .receipt-header {
            color: #00a4d3; padding: 20px 40px;
            display: flex; justify-content: space-between; align-items: center;
            border: 2px solid #00a4d3; border-radius: 8px 8px 0 0;
        }
        .receipt-header .membrete { width: 50%; }
        .receipt-header .membrete img { width: 100%; height: auto; border-radius: 6px; }
        .receipt-header .receipt-meta .label { font-size: 12px; color: #888; text-transform: uppercase; }
        .receipt-header .receipt-meta .number { font-size: 22px; font-weight: 700; color: #333; }
        .receipt-body { padding: 30px 40px; border: 1px solid #eee; border-top: none; }
        .info-row { display: flex; margin-bottom: 15px; gap: 40px; }
        .info-block { flex: 1; }
        .info-block .label { font-size: 11px; color: #888; text-transform: uppercase; margin-bottom: 4px; }
        .info-block .value { font-size: 16px; font-weight: 600; }
        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
        th { background: #f8f9fa; padding: 12px; text-align: left; font-size: 13px; color: #666; text-transform: uppercase; border-bottom: 2px solid #00a4d3; }
        td { font-size: 14px; }
        .total-section { margin-top: 20px; display: flex; justify-content: flex-end; }
        .total-box { background: #f8f9fa; padding: 15px 25px; border-radius: 6px; border-left: 4px solid #00a4d3; }
        .total-box .total-amount { font-size: 24px; font-weight: 700; color: #00a4d3; }
        .total-box .total-label { font-size: 12px; color: #888; text-transform: uppercase; }
        .observation { margin-top: 20px; padding: 12px 15px; background: #fffde8; border-radius: 4px; border-left: 3px solid #ffc107; font-size: 14px; color: #666; }
        .receipt-footer { padding: 20px 40px; background: #f8f9fa; text-align: center; font-size: 12px; color: #999; border: 1px solid #eee; border-top: none; border-radius: 0 0 8px 8px; }
        @page { size: letter; margin: 10mm; }
        @media print {
            body { padding: 0; background: #fff; }
            .toolbar { display: none; }
            .receipt-container { display: flex; flex-direction: column; min-height: 100vh; max-width: 100%; }
            .receipt-body { flex: 1; }
            .receipt-footer { margin-top: auto; }
        }
    </style>
</head>
<body>
    ${toolbarBlock}
    <div class="receipt-container" id="receipt-container">
        <div class="receipt-header">
            <div class="membrete">
                <img src="${logoUrl}" alt="Aguaman">
            </div>
            <div class="receipt-meta">
                <div class="label">Recibo N°</div>
                <div class="number">#${receipt.receiptNumber}</div>
            </div>
        </div>
        <div class="receipt-body">
            <div class="info-row">
                <div class="info-block">
                    <div class="label">Titular</div>
                    <div class="value" style="white-space: nowrap;">${receipt.holderName}</div>
                </div>
                <div class="info-block">
                    <div class="label">Fecha</div>
                    <div class="value">${formattedDate}</div>
                </div>
            </div>
            ${holderContactBlock}
            ${holderAddressBlock}
            <table>
                <thead>
                    <tr>
                        <th>#</th><th>Producto</th>
                        <th style="text-align:center;">Cant.</th>
                        <th style="text-align:right;">Precio</th>
                        <th style="text-align:right;">Subtotal</th>
                    </tr>
                </thead>
                <tbody>${itemsRows}</tbody>
            </table>
            ${receipt.observation ? `<div class="observation"><strong>Observación:</strong> ${receipt.observation}</div>` : ''}
            <div class="total-section">
                <div class="total-box">
                    <div class="total-label">Total</div>
                    <div class="total-amount">${sym} ${formatAmount(receipt.totalAmount, receipt.currency)}</div>
                    ${receipt.paymentMethod ? `<div style="margin-top:8px;font-size:13px;color:#666;">Pago: ${receipt.paymentMethod}</div>` : ''}
                </div>
            </div>
        </div>
        <div class="receipt-footer">
            <strong>Aguaman Maldonado Romer F.P.</strong><br>
            RIF: V-20608546-7<br>
            0414-7353821<br>
            aguamandelivery.com
        </div>
    </div>
    ${toolbarScript}
</body>
</html>`;
};

export const getCurrencySymbol = (currency) => {
    const symbols = { USD: '$', VES: 'Bs.', COP: '$' };
    return symbols[currency] || '$';
};

/**
 * Formatea un número según la moneda:
 * - VES y COP: formato venezolano/colombiano — miles con punto, decimales con coma
 * - USD: formato internacional — miles con coma, decimales con punto
 */
export const formatAmount = (amount, currency) => {
    const num = Number(amount) || 0;
    if (currency === 'VES' || currency === 'COP') {
        // Formato es-VE: 1.234,56
        return num.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    // USD y otros: 1,234.56
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};